import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const StyledCanvas = styled.canvas`
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
`;

const MAX_DPR = 2;
const MAX_PARTICLES = 90;
const MOBILE_MAX_PARTICLES = 40;
const MOBILE_BREAKPOINT = 768;
const CONNECT_DISTANCE = 120;
const MOUSE_INFLUENCE_RADIUS = 150;
const MAX_VELOCITY = 0.6;
const VELOCITY_DAMPING = 0.95;
const EXPLOSION_RADIUS = 220;
const EXPLOSION_FORCE = 6;
const MAX_EXPLOSION_VELOCITY = 4;
const RIPPLE_DURATION = 600; // ms
const RIPPLE_MAX_RADIUS = 140;
const MAX_RIPPLES = 5;

const hexToRgb = hex => {
  const normalized = hex.replace('#', '');
  const value =
    normalized.length === 3
      ? normalized
        .split('')
        .map(c => c + c)
        .join('')
      : normalized;
  const int = parseInt(value, 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
};

const ParticleBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    let width = window.innerWidth;
    let height = window.innerHeight;
    let dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    let particles = [];
    let ripples = [];
    let animationId = null;
    let paused = false;
    const mouse = { x: null, y: null };

    const accentColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--green')
      .trim();
    const rgb = hexToRgb(accentColor);

    const particleCount = () => {
      const cap = width <= MOBILE_BREAKPOINT ? MOBILE_MAX_PARTICLES : MAX_PARTICLES;
      return Math.min(cap, Math.floor((width * height) / 15000));
    };

    const createParticles = () => {
      particles = Array.from({ length: particleCount() }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
      }));
    };

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      createParticles();
    };

    const step = () => {
      ctx.clearRect(0, 0, width, height);

      particles.forEach(p => {
        if (mouse.x !== null) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist = Math.hypot(dx, dy);
          if (dist < MOUSE_INFLUENCE_RADIUS && dist > 0) {
            const force = (1 - dist / MOUSE_INFLUENCE_RADIUS) * 0.02;
            p.vx = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, p.vx - (dx / dist) * force));
            p.vy = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, p.vy - (dy / dist) * force));
          }
        }

        const speed = Math.hypot(p.vx, p.vy);
        if (speed > MAX_VELOCITY) {
          p.vx *= VELOCITY_DAMPING;
          p.vy *= VELOCITY_DAMPING;
        }

        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0) {
          p.x = width;
        }
        if (p.x > width) {
          p.x = 0;
        }
        if (p.y < 0) {
          p.y = height;
        }
        if (p.y > height) {
          p.y = 0;
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.6)`;
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i += 1) {
        for (let j = i + 1; j < particles.length; j += 1) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.hypot(dx, dy);
          if (dist < CONNECT_DISTANCE) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${
              0.15 * (1 - dist / CONNECT_DISTANCE)
            })`;
            ctx.lineWidth = 1;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      const now = performance.now();
      ripples = ripples.filter(r => now - r.time < RIPPLE_DURATION);
      ripples.forEach(r => {
        const t = (now - r.time) / RIPPLE_DURATION;
        const radius = t * RIPPLE_MAX_RADIUS;
        const alpha = 1 - t;
        ctx.beginPath();
        ctx.arc(r.x, r.y, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha * 0.8})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      });

      if (!paused) {
        animationId = window.requestAnimationFrame(step);
      }
    };

    let ticking = false;
    const onMouseMove = e => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          mouse.x = e.clientX;
          mouse.y = e.clientY;
          ticking = false;
        });
        ticking = true;
      }
    };
    const onTouchMove = e => {
      // Do NOT call preventDefault() here — it would block native page scroll on mobile
      if (e.touches && e.touches[0] && !ticking) {
        const touch = e.touches[0];
        window.requestAnimationFrame(() => {
          mouse.x = touch.clientX;
          mouse.y = touch.clientY;
          ticking = false;
        });
        ticking = true;
      }
    };

    const explode = (x, y) => {
      particles.forEach(p => {
        const dx = p.x - x;
        const dy = p.y - y;
        const dist = Math.hypot(dx, dy) || 1;
        if (dist < EXPLOSION_RADIUS) {
          const force = (1 - dist / EXPLOSION_RADIUS) * EXPLOSION_FORCE;
          p.vx = Math.max(
            -MAX_EXPLOSION_VELOCITY,
            Math.min(MAX_EXPLOSION_VELOCITY, p.vx + (dx / dist) * force),
          );
          p.vy = Math.max(
            -MAX_EXPLOSION_VELOCITY,
            Math.min(MAX_EXPLOSION_VELOCITY, p.vy + (dy / dist) * force),
          );
        }
      });

      ripples.push({ x, y, time: performance.now() });
      if (ripples.length > MAX_RIPPLES) {
        ripples.shift();
      }
    };

    const onClick = e => {
      explode(e.clientX, e.clientY);
    };

    let resizeTicking = false;
    const onResize = () => {
      if (!resizeTicking) {
        window.requestAnimationFrame(() => {
          resize();
          resizeTicking = false;
        });
        resizeTicking = true;
      }
    };

    const onVisibilityChange = () => {
      paused = document.hidden;
      if (!paused && animationId === null) {
        animationId = window.requestAnimationFrame(step);
      }
    };

    resize();
    animationId = window.requestAnimationFrame(step);

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('click', onClick, { passive: true });
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (animationId !== null) {
        window.cancelAnimationFrame(animationId);
      }
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('click', onClick);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return <StyledCanvas ref={canvasRef} aria-hidden="true" />;
};

export default ParticleBackground;
